import { useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { fetchJson } from "../api/http";
import { useAuth, type AuthUser } from "../contexts/AuthContext";

type RegisterResponse = { access_token: string; refresh_token: string; user?: AuthUser | null };
type LocationState = { from?: { pathname?: string } } | null;

export function Register() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = useMemo(() => {
    const st = (location.state as LocationState) ?? null;
    return st?.from?.pathname && typeof st.from.pathname === "string" ? st.from.pathname : "/home";
  }, [location.state]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetchJson<RegisterResponse>(
        "/auth/register",
        {
          method: "POST",
          body: { username: username.trim(), email: email.trim(), password },
          skipAuth: true, // Public endpoint
        },
        undefined,
      );
      await auth.setTokens({ accessToken: res.access_token, refreshToken: res.refresh_token });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Create account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Set up your OpenMon account.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
          <Stack component="form" spacing={2} onSubmit={onSubmit}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <TextField
              label="Username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              fullWidth
              disabled={submitting}
            />
            <TextField
              label="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              disabled={submitting}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              disabled={submitting}
            />

            <Button type="submit" variant="contained" size="large" disabled={submitting}>
              {submitting ? "Creating…" : "Create account"}
            </Button>

            <Divider />

            <Typography variant="body2" color="text.secondary">
              Already have an account?{" "}
              <Link component={RouterLink} to="/login" underline="hover">
                Sign in
              </Link>
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}

